import classNames from 'classnames';

import { TaxRegionKey, UsTaxImplicationsResult } from '@capintel/instruments-lib';

import { ProposalDAOInterface } from '../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../ProposalDAOImpl';
import { ProposalFieldDAO } from '../ProposalFieldDAO';
import * as ProposalNS from '../../Proposal';
import * as ProposalElementNS from '../../ProposalElements';
import { DataPoints } from '../DataPoints';
import { ProposalStyleClassNames } from '../../ProposalStyleClassNames';
import { CurrencyCell } from '@/views/components/cells';
import { ComparisonItemDAO } from '../ProposalItemDAO';
import { PROPOSAL_DISCLAIMERS } from '../../utils/constants';

/**
 * Tax Analysis Transition Implications DAO
 * Displays tax implications table with short-term, long-term, totals, and carryforward data
 */
export default class TaxAnalysisTransitionImplicationsDAO extends ComparisonItemBasedProposalDAO implements ProposalDAOInterface {
  metricsFieldClasses = classNames(ProposalStyleClassNames.ProposalTableContent, ProposalStyleClassNames.ProposalTableIndentField);

  i18nBasePath = 'Proposal.Modules.TaxAnalysis';

  constructor(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement) {
    // Filter to only include the original portfolio item before parent constructor runs
    const originalItemId = proposal.taxAnalysis?.originalComparisonItemId;

    if (originalItemId) {
      const filteredProposal = {
        ...proposal,
        items: proposal.items.filter(item => item._id === originalItemId),
      };
      super(filteredProposal, proposalElement);
    } else {
      super(proposal, proposalElement);
    }
  }

  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new TaxAnalysisTransitionImplicationsDAO(proposal, proposalElement);
  }

  protected getColorKeys(): string[] {
    return [];
  }

  protected build(): void {
    const allItems = this.getItemDAOs();

    if (allItems.length === 0) {
      return;
    }

    const itemWithTaxData = allItems[0];

    const taxData = DataPoints.getTaxAnalysisMetrics(itemWithTaxData.item);
    if (!taxData.response?.taxImplications) {
      return;
    }

    // Get region from stored taxSettings (defaults to US for backwards compatibility)
    const region = this.proposal.taxAnalysis?.taxSettings?.region;
    if (!region) {
      return;
    }

    // Extract region-specific tax implications and build render config
    const { taxImplications } = taxData.response;
    let renderConfig;

    if (TaxRegionKey.US in taxImplications) {
      renderConfig = this.itemToMetricsConfig(taxImplications[TaxRegionKey.US]);
    }

    // TODO: Add support for CA tax implications when implemented
    if (!renderConfig) {
      return;
    }

    this.addDisclaimerKeys([PROPOSAL_DISCLAIMERS.TAX_ANALYSIS], itemWithTaxData.item);

    for (const section of renderConfig) {
      const field = new ProposalFieldDAO('', section.value !== undefined ? { value: section.value } : '');
      if (section.value !== undefined) {
        field.cellType = CurrencyCell;
      }
      this.addFieldDAO(itemWithTaxData, field);

      const fieldClasses = classNames(ProposalStyleClassNames.ProposalTableGroup, ProposalStyleClassNames.ProposalTableContent, 'tax-section-header');
      this.addFieldName(section.title, fieldClasses);

      for (const field of section.fields) {
        this.addMetricField(itemWithTaxData, field.label, field.value);
      }
    }
  }

  addMetricField(itemDAO: ComparisonItemDAO, fieldName: string, fieldValue: number | null | undefined): void {
    const field = new ProposalFieldDAO(fieldName, { value: fieldValue }, this.metricsFieldClasses);
    field.cellType = CurrencyCell;
    this.addFieldDAO(itemDAO, field);
    this.addFieldName(fieldName, this.metricsFieldClasses);
  }

  itemToMetricsConfig = (taxImplications: UsTaxImplicationsResult) => {
    const { shortTerm, longTerm, capitalGainLossNet, capitalGainNetOfCarryforward, totalEstimatedTaxes, carryforwardBalance } = taxImplications;

    return [
      {
        title: this.formatMessage(`${this.i18nBasePath}.ShortTerm.Title`),
        fields: [
          {
            label: this.formatMessage(`${this.i18nBasePath}.ShortTerm.Gains`),
            value: shortTerm.gains,
          },
          {
            label: this.formatMessage(`${this.i18nBasePath}.ShortTerm.Loss`),
            value: shortTerm.losses,
          },
          {
            label: this.formatMessage(`${this.i18nBasePath}.ShortTerm.Total`),
            value: shortTerm.totalGainLoss,
          },
        ],
      },
      {
        title: this.formatMessage(`${this.i18nBasePath}.LongTerm.Title`),
        fields: [
          {
            label: this.formatMessage(`${this.i18nBasePath}.LongTerm.Gains`),
            value: longTerm.gains,
          },
          {
            label: this.formatMessage(`${this.i18nBasePath}.LongTerm.Loss`),
            value: longTerm.losses,
          },
          {
            label: this.formatMessage(`${this.i18nBasePath}.LongTerm.Total`),
            value: longTerm.totalGainLoss,
          },
        ],
      },
      {
        title: this.formatMessage(`${this.i18nBasePath}.CapitalGainLossNet.Title`),
        value: capitalGainLossNet,
        fields: [],
      },
      {
        title: this.formatMessage(`${this.i18nBasePath}.CapitalGainNetOfCarryforward.Title`),
        value: capitalGainNetOfCarryforward,
        fields: [],
      },
      {
        title: this.formatMessage(`${this.i18nBasePath}.TotalEstimatedTaxes.Title`),
        value: totalEstimatedTaxes,
        fields: [],
      },
      {
        title: this.formatMessage(`${this.i18nBasePath}.CarryForward.Title`),
        fields: [
          {
            label: this.formatMessage(`${this.i18nBasePath}.CarryForward.ShortTerm`),
            value: carryforwardBalance.shortTerm,
          },
          {
            label: this.formatMessage(`${this.i18nBasePath}.CarryForward.LongTerm`),
            value: carryforwardBalance.longTerm,
          },
        ],
      },
    ];
  };
}
